import React, { useState } from 'react'
import styled from 'styled-components'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  align-self: stretch;
`

const StyledLabel = styled.label`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
  color: #364153;
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`

const Required = styled.span`
  color: #364153;
  margin-right: 2px;
`

const StyledInput = styled.input`
  display: flex;
  height: 41.804px;
  padding: 8px 16px;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  color: rgba(15, 23, 42, 0.50);
  text-align: right;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  outline: none;
  width: 100%;
  box-sizing: border-box;
`

const StyledSelect = styled.select`
  display: flex;
  height: 41.804px;
  padding: 8px 16px;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  color: rgba(15, 23, 42, 0.50);
  text-align: right;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
`

const ColorList = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const ColorButton = styled.button<{ bg: string; selected: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ bg }) => bg};
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  box-shadow: ${({ selected }) =>
    selected ? '0 0 0 2px #FFF, 0 0 0 4px #101828' : 'none'};
`

type ColorPickerProps = {
  label: string
  required?: boolean
  value: string
  onChange: (color: string) => void
  colors: string[]
}

export const ColorPicker: React.FC<ColorPickerProps> = ({ label, required = false, value, onChange, colors }) => (
  <Wrapper>
    <StyledLabel>
      {label}
      {required && <Required>*</Required>}
    </StyledLabel>
    <ColorList>
      {colors.map((color, i) => (
        <ColorButton
          key={`${color}-${i}`}
          bg={color}
          selected={value === color}
          onClick={() => onChange(color)}
          type="button"
        />
      ))}
    </ColorList>
  </Wrapper>
)

type InputProps = {
  label: string
  required?: boolean
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
}

type SelectProps = {
  label: string
  required?: boolean
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  disabled?: boolean
}

export const FormInput: React.FC<InputProps> = ({
  label,
  required = false,
  value,
  onChange,
  placeholder = '',
  disabled = false,
}) => (
  <Wrapper>
    <StyledLabel>
      {label}{required && <Required>*</Required>}
    </StyledLabel>
    <StyledInput
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
    />
  </Wrapper>
)

export const FormSelect: React.FC<SelectProps> = ({
  label,
  required = false,
  value,
  onChange,
  options,
  disabled = false,
}) => (
  <Wrapper>
    <StyledLabel>
      {label}{required && <Required>*</Required>}
    </StyledLabel>
    <StyledSelect
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </StyledSelect>
  </Wrapper>
)

const SearchWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  align-self: stretch;
  position: relative;
`

const SelectedTags = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
  align-self: stretch;
`

const Tag = styled.div<{ tagColor?: string; tagBg?: string }>`
  display: flex;
  padding: 4px 12px 4px 12px;
  justify-content: center;
  align-items: center;
  gap: 3.991px;
  border-radius: 30px;
  background: ${({ tagBg }) => tagBg || '#DBEAFE'};
  color: ${({ tagColor }) => tagColor || '#1447E6'};
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`

const TagRemove = styled.button<{ tagColor?: string; }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${({ tagColor }) => tagColor || '#1447E6'};
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 1;
`

const DropdownList = styled.div`
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 10;
  background: #fff;
  border: 0.909px solid #D1D5DC;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
`
const DropdownItem = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  font-family: Rubik;
  font-size: 14px;
  color: #0F172A;
  text-align: right;
  &:hover {
    background: #EFF6FF;
  }
`

export type UserOption = {
  userId: number
  fullName: string
}

type FormSearchProps = {
  label: string
  required?: boolean
  value: UserOption[]
  onChange: (value: UserOption[]) => void
  options: UserOption[]
  placeholder?: string
  tagColor?: string
  tagBg?: string
}

export const FormSearch: React.FC<FormSearchProps> = ({
  label,
  required = false,
  value,
  onChange,
  options,
  placeholder = 'חיפוש...',
  tagColor,
  tagBg,
}) => {
  const [search, setSearch] = useState('')

  const filtered = options
    .filter(o => !value.some(v => v.userId === o.userId) && o.fullName.includes(search))
    .slice(0, 5)

  const addItem = (item: UserOption) => {
    onChange([...value, item])
    setSearch('')
  }

  const removeItem = (userId: number) => {
    onChange(value.filter(v => v.userId !== userId))
  }

  return (
    <SearchWrapper>
      {value.length > 0 && (
        <SelectedTags>
          {value.map(item => (
            <Tag key={item.userId} tagColor={tagColor} tagBg={tagBg}>
              {item.fullName}
              <TagRemove type="button" tagColor={tagColor} onClick={() => removeItem(item.userId)}>×</TagRemove>
            </Tag>
          ))}
        </SelectedTags>
      )}
      <StyledInput
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder={placeholder}
      />
      {search && filtered.length > 0 && (
        <DropdownList>
          {filtered.map(item => (
            <DropdownItem key={item.userId} onMouseDown={e => e.preventDefault()} onClick={() => addItem(item)}>
              {item.fullName}
            </DropdownItem>
          ))}
        </DropdownList>
      )}
    </SearchWrapper>
  )
}
